import LoadingScreen from '@/components/LoadingScreen'
import { useState, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from '@/hooks/useRedux'
import { setUser } from '@/stores/authSlice'
import { userService } from '@/services/api'
import type { User } from '@/types'

export default function SettingsPage() {
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const [profile, setProfile] = useState<Partial<User>>({})
  const [activeTab, setActiveTab] = useState<'profile' | 'appearance' | 'privacy'>('profile')
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (user) {
      setProfile(user)
    }
  }, [user])

  const handleSaveProfile = async () => {
    if (!user) return
    setIsSaving(true)
    try {
      const response = await userService.updateProfile(user.id, profile)
      dispatch(setUser(response.data))
      setMessage('Profile updated successfully')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      setMessage('Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  if (!user) {
    return <LoadingScreen />
  }

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <h1 className="text-4xl font-bold text-white mb-8">Settings</h1>

      {message && (
        <div className="bg-green-900 border border-green-700 text-green-200 px-4 py-3 rounded mb-4">
          {message}
        </div>
      )}

      <div className="flex gap-8">
        {/* Sidebar */}
        <div className="w-48">
          <nav className="space-y-2">
            {(['profile', 'appearance', 'privacy'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`w-full text-left px-4 py-2 rounded capitalize ${
                  activeTab === tab
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 max-w-2xl">
          {activeTab === 'profile' && (
            <div className="bg-gray-800 rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-bold text-white mb-4">Profile Settings</h2>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={profile.display_name || ''}
                  onChange={(e) =>
                    setProfile({ ...profile, display_name: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={profile.username || ''}
                  onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Custom Status
                </label>
                <input
                  type="text"
                  value={profile.custom_status || ''}
                  onChange={(e) =>
                    setProfile({ ...profile, custom_status: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  placeholder="What's your status?"
                />
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-bold py-2 px-6 rounded"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {activeTab === 'appearance' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Appearance</h2>
              <p className="text-gray-400">Theme settings coming soon...</p>
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Privacy & Safety</h2>
              <p className="text-gray-400">Privacy settings coming soon...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
